import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquare, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { usePreferences } from "@/hooks/usePreferences";
import { cn } from "@/lib/utils";

type FeedbackType = "bug" | "confusing" | "suggestion" | "other";

const TYPES: { value: FeedbackType; label: string }[] = [
  { value: "bug", label: "Bug" },
  { value: "confusing", label: "Confusing" },
  { value: "suggestion", label: "Suggestion" },
  { value: "other", label: "Other" },
];

/**
 * Best-effort browser/OS/device parsing from the UA string. We don't need
 * pixel-perfect accuracy here — these labels are just to help triage
 * feedback later. The full UA string is also captured separately.
 */
const detectClient = () => {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  let browser = "Unknown";
  if (/edg\//i.test(ua)) browser = "Edge";
  else if (/chrome|crios/i.test(ua) && !/edg\//i.test(ua)) browser = "Chrome";
  else if (/firefox|fxios/i.test(ua)) browser = "Firefox";
  else if (/safari/i.test(ua) && !/chrome|crios/i.test(ua)) browser = "Safari";

  let os = "Unknown";
  if (/windows nt/i.test(ua)) os = "Windows";
  else if (/mac os x/i.test(ua) && !/iphone|ipad|ipod/i.test(ua)) os = "macOS";
  else if (/iphone|ipod/i.test(ua)) os = "iOS";
  else if (/ipad/i.test(ua)) os = "iPadOS";
  else if (/android/i.test(ua)) os = "Android";
  else if (/linux/i.test(ua)) os = "Linux";

  let device_type: "mobile" | "tablet" | "desktop" = "desktop";
  if (/ipad|tablet/i.test(ua)) device_type = "tablet";
  else if (/mobi|android|iphone|ipod/i.test(ua)) device_type = "mobile";

  return { browser, os, device_type, user_agent: ua };
};

export const FeedbackWidget = () => {
  const location = useLocation();
  // The Display "Aa" button only renders when the user is signed in. We
  // mirror that condition here so the Feedback button only shifts upward
  // when there is actually a Display button beneath it. Otherwise we sit
  // at the standard bottom-right anchor.
  const { signedInEmail, subscriptionChecked } = usePreferences();
  const hasDisplayButton = Boolean(signedInEmail) && subscriptionChecked;

  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [type, setType] = useState<FeedbackType | null>(null);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Pre-fill the email field with the signed-in email when known.
  useEffect(() => {
    if (signedInEmail && !email) setEmail(signedInEmail);
  }, [signedInEmail, email]);

  // Hide the widget on print routes — feedback button on a printable
  // shopping list / menu would clutter the printout.
  if (location.pathname.endsWith("/print")) return null;

  const reset = () => {
    setComment("");
    setType(null);
    setSubmitting(false);
  };

  const handleSubmit = async () => {
    const trimmed = comment.trim();
    if (!trimmed) {
      toast.error("Please describe what happened.");
      return;
    }
    setSubmitting(true);

    const client = detectClient();
    const payload = {
      comment: trimmed,
      feedback_type: type,
      user_email: email.trim() || signedInEmail || null,
      page_route: window.location.pathname + window.location.search,
      page_title: typeof document !== "undefined" ? document.title : null,
      browser: client.browser,
      os: client.os,
      device_type: client.device_type,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      screen_width: window.screen?.width ?? null,
      screen_height: window.screen?.height ?? null,
      user_agent: client.user_agent,
      app_version: import.meta.env.MODE,
    };

    const { error } = await supabase.functions.invoke("submit-feedback", {
      body: payload,
    });

    if (error) {
      console.error("[feedback] submit failed", error);
      toast.error("Couldn't send feedback. Please try again.");
      setSubmitting(false);
      return;
    }

    toast.success("Thanks — your feedback has been sent.");
    setOpen(false);
    // Defer reset so the closing animation doesn't flash empty content.
    setTimeout(reset, 200);
  };

  // Recipe pages render a fixed bottom action bar (Share / Save). When we're
  // on one of those pages, lift both floating buttons further so they don't
  // sit on top of the bar.
  const onRecipePage =
    location.pathname.startsWith("/recipe") || location.pathname.startsWith("/r/");
  const bottomClass = onRecipePage
    ? hasDisplayButton
      ? "bottom-40"
      : "bottom-24"
    : hasDisplayButton
      ? "bottom-24"
      : "bottom-6";

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!submitting) setOpen(next);
      }}
    >
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label="Send feedback"
          className={cn(
            "fixed right-6 z-40 flex items-center justify-center gap-2 rounded-full",
            "bg-secondary text-secondary-foreground shadow-lift transition-smooth",
            // Soften so the button stays out of the way of content;
            // restore on hover/focus/active so it never feels broken.
            "opacity-70 hover:opacity-100 focus-visible:opacity-100 active:opacity-100",
            "hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2",
            "focus-visible:ring-ring focus-visible:ring-offset-2",
            // Mobile: icon-only circle (matches the Aa button's footprint).
            // sm+: full pill with label.
            "h-11 w-11 px-0 sm:h-11 sm:w-auto sm:px-4 text-sm font-medium",
            bottomClass,
          )}
        >
          <MessageSquare className="h-4 w-4" aria-hidden="true" />
          <span className="hidden sm:inline">Feedback</span>
        </button>
      </SheetTrigger>
      <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto sm:max-w-lg sm:mx-auto sm:rounded-t-xl">
        <SheetHeader className="text-left">
          <SheetTitle>Help improve CleanPlate</SheetTitle>
          <SheetDescription>
            Spot a bug or something confusing? Tell us quickly.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="feedback-comment">What happened?</Label>
            <Textarea
              id="feedback-comment"
              placeholder="What happened, or what could be better?"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={5}
              maxLength={5000}
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label>Type (optional)</Label>
            <div className="flex flex-wrap gap-2">
              {TYPES.map((t) => {
                const active = type === t.value;
                return (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setType(active ? null : t.value)}
                    className={cn(
                      "rounded-full border px-3 py-1 text-sm transition-smooth",
                      active
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-background text-foreground hover:bg-accent",
                    )}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {!signedInEmail && (
            <div className="space-y-2">
              <Label htmlFor="feedback-email">Email (optional)</Label>
              <Input
                id="feedback-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              type="button"
              onClick={() => setOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleSubmit} disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Send feedback
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
